import dayjs from 'dayjs'

// A `pending` snapshot only suppresses events while the local mutation is in flight. Keep its TTL
// short so a stuck/failed mutation can't silently hide real Pusher events for the full 15s.
const PENDING_ECHO_TTL_MS = 4_000

export type PendingMutationKind = 'upsert' | 'delete' | 'status'

export type ScheduleSlotSnapshot = {
  date: string
  start_time: string
  end_time: string
  room: string
}

/**
 * Lightweight snapshot of a local mutation in-flight. This does not attempt to be a full
 * pusher de-duplication mechanism; it only classifies incoming upserts relative to the
 * local optimistic move so merge logic can prevent stale slot reversions.
 */
export type PendingMutationSnapshot = {
  kind?: PendingMutationKind
  previous?: ScheduleSlotSnapshot
  intended?: ScheduleSlotSnapshot
}

type PendingMutation = {
  count: number
  expiresAt: number
  kind: PendingMutationKind
  previous?: ScheduleSlotSnapshot
  intended?: ScheduleSlotSnapshot
}

const pendingMutations = new Map<number, PendingMutation>()

export const normalizeScheduleTime = (value?: string | Date | null): string => {
  if (!value) return ''
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format('HH:mm:ss') : String(value)
}

const resolveRoomCode = (
  room?: { code?: string | number; value?: string | number } | string | number | null,
): string => {
  if (room == null) return ''
  if (typeof room === 'string' || typeof room === 'number') return String(room)
  const raw = room.code ?? room.value
  return raw != null ? String(raw) : ''
}

export const toScheduleSlotSnapshot = (data: {
  date?: string | null
  start_time?: string | Date | null
  end_time?: string | Date | null
  room?: { code?: string; value?: string } | string | null
}): ScheduleSlotSnapshot => ({
  date: data.date ?? '',
  start_time: normalizeScheduleTime(data.start_time),
  end_time: normalizeScheduleTime(data.end_time),
  room: resolveRoomCode(data.room),
})

export const scheduleSlotsMatch = (
  left: ScheduleSlotSnapshot,
  right: ScheduleSlotSnapshot,
): boolean =>
  left.date === right.date &&
  left.start_time === right.start_time &&
  left.end_time === right.end_time &&
  left.room === right.room

const isExpired = (expiresAt: number) => Date.now() > expiresAt

const getPendingMutation = (appointmentId: number): PendingMutation | undefined => {
  const current = pendingMutations.get(appointmentId)
  if (!current) return undefined
  if (isExpired(current.expiresAt) || current.count <= 0) {
    pendingMutations.delete(appointmentId)
    return undefined
  }
  return current
}

export const markScheduleMutationPending = (
  appointmentId: number,
  snapshot: PendingMutationSnapshot = {},
) => {
  const current = getPendingMutation(appointmentId)
  pendingMutations.set(appointmentId, {
    count: (current?.count ?? 0) + 1,
    expiresAt: Date.now() + PENDING_ECHO_TTL_MS,
    kind: snapshot.kind ?? current?.kind ?? 'upsert',
    previous: current?.previous ?? snapshot.previous,
    intended: snapshot.intended ?? current?.intended,
  })
}

export const settleScheduleMutationPending = (appointmentId: number) => {
  const current = pendingMutations.get(appointmentId)
  if (!current) return
  if (current.count <= 1 || isExpired(current.expiresAt)) {
    pendingMutations.delete(appointmentId)
    return
  }
  pendingMutations.set(appointmentId, { ...current, count: current.count - 1 })
}

export const hasPendingScheduleMutation = (appointmentId: number): boolean =>
  getPendingMutation(appointmentId) != null

export const hasAnyPendingScheduleMutation = (): boolean => {
  for (const appointmentId of pendingMutations.keys()) {
    if (getPendingMutation(appointmentId)) return true
  }
  return false
}

export type PendingPatientPayloadKind = 'none' | 'stale' | 'intended' | 'remote'

/**
 * Classify an incoming appointment against the local pending mutation:
 * - stale: previous slot (ignore structural fields)
 * - intended: our own optimistic target (ignore structural fields)
 * - remote: different slot while mutation pending (allow structural merge)
 */
export const classifyPendingPatientUpsert = (data: {
  id: number
  date?: string | null
  start_time?: string | Date | null
  end_time?: string | Date | null
  room?: { code?: string; value?: string } | string | null
}): PendingPatientPayloadKind => {
  const pending = getPendingMutation(data.id)
  if (!pending) return 'none'
  if (pending.kind !== 'upsert') return 'remote'

  const incoming = toScheduleSlotSnapshot(data)
  if (pending.previous && scheduleSlotsMatch(incoming, pending.previous)) return 'stale'
  if (pending.intended && scheduleSlotsMatch(incoming, pending.intended)) return 'intended'
  if (pending.previous || pending.intended) return 'remote'
  return 'remote'
}

export const resetSchedulePusherEcho = () => {
  pendingMutations.clear()
}
